<template>
  <div
    ref="scroller"
    class="ai-chat-message-list"
  >
    <div
      v-if="loading && messages.length === 0"
      class="_spinner"
    >
      <m-spinner size="xs" />
    </div>
    <div
      v-else-if="messages.length === 0 && !pending"
      class="_placeholder"
    >
      {{ $t('aiChat.emptyState') }}
    </div>
    <template v-else>
      <ai-chat-message
        v-for="(message, index) in messages"
        :key="message.uid"
        :message="message"
        :retryable="retryable && index === messages.length - 1"
        @retry="$emit('retry')"
      />
      <ai-chat-message
        v-if="outgoing !== null"
        :message="outgoing"
        sending
      />
      <div
        v-if="pending && outgoing === null"
        class="_pending"
      >
        {{ $t('aiChat.thinking') }}
      </div>
    </template>
  </div>
</template>

<script setup>
import AiChatMessage from '@/components/ai-chat/AiChatMessage.vue';
import { nextTick, ref, watch } from 'vue';

const props = defineProps({
  messages: {
    type: Array,
    required: true,
  },
  outgoing: {
    type: Object,
    default: null,
  },
  pending: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  retryable: {
    type: Boolean,
    default: false,
  },
});

defineEmits(['retry']);

const scroller = ref(null);

const scrollToBottom = () => nextTick(() => {
  if (scroller.value === null) {
    return;
  }
  scroller.value.scrollTop = scroller.value.scrollHeight;
});

watch(() => [props.messages.length, props.outgoing, props.pending], scrollToBottom, { immediate: true });
</script>

<style
  scoped
  lang="scss"
  type="text/scss"
>
  .ai-chat-message-list {
    display: flex;
    flex-direction: column;
    gap: .8rem;
    flex: 1;
    min-height: 0;

    // the bottom has to clear the composer's fade, or the last message stays washed out once
    // there is nothing left to scroll
    padding: 1.2rem 2rem $ai-chat-composer-fade-height;
    overflow-y: auto;

    ._spinner {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 100%;
    }

    ._placeholder {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 100%;
      padding: 2rem;
      text-align: center;
      color: $font-color-secondary;
    }

    ._pending {
      padding: .4rem 0;
      color: $font-color-secondary;
    }
  }
</style>
